import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import AdminSidebar from '@/pages/admin/dashboard/components/AdminSidebar';
import { Navigate } from 'react-router-dom';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  created_at: string;
}

const ROLES = ['admin', 'editor', 'viewer'];

const emptyForm = {
  email: '',
  name: '',
  password: '',
  role: 'editor' as string,
};

export default function AdminUsersPage() {
  const { isAdmin, profile: currentProfile } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Invite form state
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showPass, setShowPass] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<UserProfile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchUsers = async () => {
    setFetchError(null);
    try {
      // Refresh session first to avoid stale JWT
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        setFetchError('Your session has expired. Please log in again.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('fetchUsers error:', error.message, error.code, error.details);
        setFetchError(`Failed to load users: ${error.message} (${error.code || 'unknown'})`);
        setUsers([]);
      } else {
        setUsers(data ?? []);
        setFetchError(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.error('fetchUsers exception:', err);
      setFetchError(`Network error: ${msg}`);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/admin/dashboard" replace />;

  const updateRole = async (id: string, role: string) => {
    await supabase.from('user_profiles').update({ role }).eq('id', id);
    setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(null);

    if (!form.email || !form.name || !form.password) {
      setInviteError('Please fill in all fields.');
      return;
    }
    if (form.password.length < 6) {
      setInviteError('Password must be at least 6 characters.');
      return;
    }

    setInviting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const functionUrl = `${supabaseUrl}/functions/v1/invite-user`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string,
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(functionUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          name: form.name.trim(),
          role: form.role,
        }),
      });

      const resData = await res.json();

      if (!res.ok) {
        console.error('Edge function error:', res.status, resData);
        setInviteError(resData?.error || `Server responded with ${res.status}`);
        return;
      }

      if (resData?.error) {
        setInviteError(resData.error);
        return;
      }

      setInviteSuccess(`${form.name} has been invited as ${form.role}! They can now log in at /admin.`);
      setForm(emptyForm);
      setShowInvite(false);
      fetchUsers();
      setTimeout(() => setInviteSuccess(null), 6000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong';
      console.error('Invite error:', err);
      setInviteError(`Network error: ${msg}. Make sure you are logged in as admin.`);
    } finally {
      setInviting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setDeleteError('Session expired. Please log in again.');
        setDeleting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const functionUrl = `${supabaseUrl}/functions/v1/delete-user`;

      const res = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string,
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userId: deleteTarget.id }),
      });

      const resData = await res.json();

      if (!res.ok || resData?.error) {
        setDeleteError(resData?.error || `Failed to delete user (${res.status})`);
        setDeleting(false);
        return;
      }

      // Remove from local state instantly
      setUsers(prev => prev.filter(u => u.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleting(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setDeleteError(`Network error: ${msg}`);
      setDeleting(false);
    }
  };

  const roleColor = (r: string) => {
    const map: Record<string, string> = {
      admin: 'bg-[#1B2838] text-white',
      editor: 'bg-[#3B9EF5]/10 text-[#3B9EF5]',
      viewer: 'bg-gray-100 text-gray-500',
    };
    return map[r] ?? 'bg-gray-100 text-gray-500';
  };

  return (
    <div className="flex min-h-screen bg-[#F5F7FA]">
      <AdminSidebar />
      <main className="flex-1 p-8 overflow-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1B2838]">Team Members</h1>
            <p className="text-gray-500 text-sm mt-0.5">Manage admin and editor access</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchUsers}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-sm cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-50 whitespace-nowrap"
              title="Refresh user list"
            >
              <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            </button>
            <button
              onClick={() => { setShowInvite(!showInvite); setInviteError(null); setInviteSuccess(null); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#3B9EF5] text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors whitespace-nowrap"
            >
              <i className={`${showInvite ? 'ri-close-line' : 'ri-user-add-line'}`}></i>
              {showInvite ? 'Cancel' : 'Invite Team Member'}
            </button>
          </div>
        </div>

        {/* Success message */}
        {inviteSuccess && (
          <div className="mb-6 px-4 py-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-sm flex items-start gap-2">
            <i className="ri-check-line text-emerald-500 mt-0.5 flex-shrink-0"></i>
            {inviteSuccess}
          </div>
        )}

        {/* Invite form */}
        {showInvite && (
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-[#1B2838] mb-5">Invite a New Team Member</h2>

            {inviteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm text-red-700 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                {inviteError}
              </div>
            )}

            <form onSubmit={handleInvite}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="colleague@example.com"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B9EF5] focus:ring-1 focus:ring-[#3B9EF5] transition-colors"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">Full Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Sarah Ahmadi"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B9EF5] focus:ring-1 focus:ring-[#3B9EF5] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">Role *</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B9EF5] focus:ring-1 focus:ring-[#3B9EF5] transition-colors cursor-pointer capitalize"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="capitalize">{r}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {form.role === 'admin' && 'Full access — can manage users, content, and settings.'}
                    {form.role === 'editor' && 'Can edit content but cannot manage other users.'}
                    {form.role === 'viewer' && 'Read-only access to the admin dashboard.'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1B2838] mb-1.5">Password *</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Min. 6 characters"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#3B9EF5] focus:ring-1 focus:ring-[#3B9EF5] transition-colors pr-12"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPass ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Share this password with your colleague — they'll use it to sign in at /admin.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-6 py-2.5 bg-[#3B9EF5] text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-[#2A8DE0] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                >
                  {inviting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Inviting...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Send Invitation
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowInvite(false); setInviteError(null); }}
                  className="px-6 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              Loading users...
            </div>
          ) : fetchError ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <i className="ri-error-warning-line text-3xl text-amber-500"></i>
              </div>
              <p className="text-sm text-gray-600 mb-2">{fetchError}</p>
              <button
                onClick={fetchUsers}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#3B9EF5] hover:bg-[#3B9EF5]/5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Retry
              </button>
            </div>
          ) : users.length === 0 ? (
            <div className="p-12 text-center text-gray-400">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3"><i className="ri-user-line text-3xl"></i></div>
              <p className="text-sm">No team members yet</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Change Role</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1B2838] text-white text-sm font-bold flex-shrink-0">
                          {u.name?.charAt(0).toUpperCase() ?? u.email?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-[#1B2838]">{u.name ?? '—'}</div>
                          <div className="text-xs text-gray-400">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${roleColor(u.role)}`}>{u.role}</span>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-400">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-right">
                      {u.id === currentProfile?.id ? (
                        <span className="text-xs text-gray-400 italic">You</span>
                      ) : (
                        <select
                          value={u.role}
                          onChange={e => updateRole(u.id, e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-[#3B9EF5] cursor-pointer capitalize"
                        >
                          {ROLES.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {u.id !== currentProfile?.id && (
                        <button
                          onClick={() => { setDeleteTarget(u); setDeleteError(null); }}
                          className="w-8 h-8 flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          title={`Delete ${u.name || u.email}`}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Delete confirmation modal */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => { if (!deleting) setDeleteTarget(null); }}></div>
            <div className="relative bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                  <i className="ri-alert-line text-red-500 text-lg"></i>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#1B2838]">Delete Team Member</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to remove <strong>{deleteTarget.name || deleteTarget.email}</strong>? This action cannot be undone. They will lose all access immediately.
                  </p>

                  {deleteError && (
                    <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
                      {deleteError}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="px-4 py-2.5 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm cursor-pointer hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold text-sm cursor-pointer hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line"></i>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}